<template>
  <div class="home">
    <div class="container">
      <!-- Заголовок -->
      <h1>Видеозвонки</h1>
      <p class="subtitle">Надежная P2P связь, работает в любых условиях</p>

      <!-- Карточка создания комнаты -->
      <div class="card">
        <h2>Создать комнату</h2>

        <!-- Форма ввода имени -->
        <div class="form-group">
          <label for="displayName">Ваше имя</label>
          <input
            id="displayName"
            v-model="displayName"
            type="text"
            placeholder="Введите имя"
            @keyup.enter="handleCreateRoom"
          />
        </div>

        <!-- Кнопка создания -->
        <button
          @click="handleCreateRoom"
          :disabled="loading"
          class="btn-primary"
        >
          {{ loading ? 'Создание...' : 'Создать комнату' }}
        </button>

        <!-- Сообщение об ошибке -->
        <div v-if="error" class="error">
          {{ error }}
        </div>

        <!-- Успешное создание комнаты -->
        <div v-if="roomUrl" class="success">
          <h3>Комната создана!</h3>

          <!-- Поле с URL для копирования -->
          <div class="url-box">
            <input
              ref="urlInput"
              :value="roomUrl"
              readonly
              @click="selectUrl"
            />
            <button @click="handleCopyUrl" class="btn-copy">
              {{ copied ? '✓' : 'Копировать' }}
            </button>
          </div>

          <!-- Кнопка входа в созданную комнату -->
          <button @click="handleJoinCreatedRoom" class="btn-primary">
            Войти в комнату
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Главная страница приложения
 *
 * Позволяет:
 * - Создать новую комнату для видеозвонка
 * - Скопировать ссылку для приглашения
 * - Войти в созданную комнату
 */

import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useRoomApi } from '../composables/useRoomApi';
import { COPY_FEEDBACK_DURATION } from '../constants';

// ==================== ИНИЦИАЛИЗАЦИЯ ====================

const router = useRouter();

/** Composable для работы с API комнат */
const { loading, error, createRoom } = useRoomApi();

// ==================== СОСТОЯНИЕ ====================

/** Имя пользователя */
const displayName = ref<string>('');

/** URL созданной комнаты */
const roomUrl = ref<string>('');

/** Токен созданной комнаты */
const roomToken = ref<string>('');

/** Флаг успешного копирования */
const copied = ref<boolean>(false);

/** Ссылка на input элемент с URL */
const urlInput = ref<HTMLInputElement | null>(null);

// ==================== ОБРАБОТЧИКИ ====================

/**
 * Создает новую комнату
 */
async function handleCreateRoom(): Promise<void> {
  const data = await createRoom(displayName.value);

  if (data) {
    roomUrl.value = data.joinUrl;
    roomToken.value = data.token;
  }
}

/**
 * Выделяет текст URL для удобного копирования
 */
function selectUrl(): void {
  urlInput.value?.select();
}

/**
 * Копирует URL в буфер обмена
 */
async function handleCopyUrl(): Promise<void> {
  try {
    await navigator.clipboard.writeText(roomUrl.value);
    copied.value = true;

    // Сбрасываем флаг через заданное время
    setTimeout(() => {
      copied.value = false;
    }, COPY_FEEDBACK_DURATION);
  } catch (err) {
    console.error('❌ Ошибка копирования:', err);
  }
}

/**
 * Переходит в созданную комнату
 */
function handleJoinCreatedRoom(): void {
  const roomId = roomUrl.value.split('/').pop();
  if (roomId) {
    router.push(`/r/${roomId}`);
  }
}
</script>

<style scoped>
/* ==================== ОСНОВНЫЕ СТИЛИ ==================== */

.home {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.container {
  max-width: 500px;
  width: 100%;
}

/* ==================== ЗАГОЛОВКИ ==================== */

h1 {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  text-align: center;
}

.subtitle {
  text-align: center;
  opacity: 0.9;
  margin-bottom: 2rem;
}

/* ==================== КАРТОЧКА ==================== */

.card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.card h2 {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
}

/* ==================== ФОРМА ==================== */

.form-group {
  margin-bottom: 1.5rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

input[type="text"] {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s;
}

input[type="text"]:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.15);
}

input[type="text"]::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

/* ==================== КНОПКИ ==================== */

.btn-primary {
  width: 100%;
  padding: 0.875rem;
  background: #fff;
  color: #667eea;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ==================== СООБЩЕНИЯ ==================== */

.error {
  margin-top: 1rem;
  padding: 0.75rem;
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.5);
  border-radius: 8px;
  color: #fecaca;
}

.success {
  margin-top: 1.5rem;
  padding: 1rem;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 8px;
}

.success h3 {
  margin-bottom: 1rem;
  color: #86efac;
}

/* ==================== URL КОПИРОВАНИЕ ==================== */

.url-box {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.url-box input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.875rem;
}

.btn-copy {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
}

.btn-copy:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
